import { useState, useEffect } from "react";
import { Button } from "src/@/components/ui/button";
import { Card } from "src/@/components/ui/card";
import { Switch } from "src/@/components/ui/switch";
import Iconify from "src/components/Iconify";
import useAuth from "src/hooks/useAuth";
import v2_actionAutomacoes from "src/actions/v2/automacoes/v2_actionAutomacoes";
import DialogCriarAutomacao from "src/sections/@dashboard/automacoes/DialogCriarAutomacao";
import { useNavigate } from "react-router";
import { getFriendlyName } from "src/utils/automacoes.utils";
import { DialogLogsAutomacao } from "src/sections/@dashboard/automacoes/DialogLogsAutomacao";
import v2_actionDeletarAutomacao from "src/actions/v2/automacoes/v2_actionDeletarAutomacao";
import { Badge } from "src/@/components/ui/badge";

const CardAutomacao = ({ automacao, onSelect, onDelete }) => (
  <Card
    onClick={onSelect}
    className="p-4 flex items-center justify-between cursor-pointer hover:border hover:border-violet-200 hover:bg-violet-50 "
  >
    <div>
      <h4 className="text-sm font-semibold">{automacao?.name}</h4>
      {automacao?.draft ? (
        <span className="text-xs text-gray-500">Rascunho</span>
      ) : (
        <div className="flex items-center space-x-2 mt-4">
          {/* Origem */}
          <div className="bg-violet-100 rounded px-3 py-1 text-xs">
            teste
            {getFriendlyName(automacao?.params?.from?.action)}
          </div>
          <Iconify icon="mdi:arrow-right" className="text-gray-500" width={20} />
          {/* Destino */}
          <div className="bg-blue-100 rounded px-3 py-1 text-xs flex items-center space-x-2">
            <span>
              teste
              {automacao?.params?.to?.length > 0 && getFriendlyName(automacao?.params?.to[0].value)}
            </span>
          
          </div>
          {automacao?.params?.to?.length > 1 && (
              <Badge variant="outline">
                +{automacao.params.to.length - 1}
              </Badge>
            )}
        </div>
      )}
    </div>
    <div className="flex flex-row items-center gap-2">
      <Button
        size="icon"
        variant="outline"
        onClick={(event) => {
          event.stopPropagation();
          onDelete();
        }}
      >
        <Iconify icon="material-symbols:delete-outline" className="text-red-400 text-[24px]" />
      </Button>
      {!automacao?.draft && (
        <Switch
          checked={automacao?.status}
          onClick={(event) => {
            event.stopPropagation();
            alert("Toggled status!");
          }}
        />
      )}
    </div>
  </Card>
);


export default function Automacoes() {
  const { cliente, user } = useAuth();
  const [automacoes, setAutomacoes] = useState([]);
  const [filteredAutomacoes, setFilteredAutomacoes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [sortOrder, setSortOrder] = useState("recent");
  const navigate = useNavigate();

  async function getAutomacoes() {
    try {
      const response = await v2_actionAutomacoes(cliente._id);
      setAutomacoes(response.data.data);
      setFilteredAutomacoes(response.data.data); // Initialize filtered automations
    } catch (err) {
      console.log(err);
    }
  }

  async function deleteAutomacao(id) {
    try {
      await v2_actionDeletarAutomacao(id);
      getAutomacoes();
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    getAutomacoes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [statusFilter, sortOrder]);

  const applyFilters = () => {
    let filtered = [...automacoes];

    // Filter by status
    if (statusFilter !== "todos") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((automacao) => automacao.status === isActive);
    }

    // Sort alphabetically or by recent
    if (sortOrder === "alphabetical") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === "recent") {
      filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)); // Assuming updatedAt exists
    }

    setFilteredAutomacoes(filtered);
  };

  return (
    <div className="lg:container lg:mx-auto">
      <div className="p-6 space-y-2">
        <div className="flex justify-between items-center py-4">
          {/* Filters */}
          <div className="flex space-x-4">
            {/* Filter by Status */}
            <div className="flex items-center space-x-2">
              <h3>Automações</h3>
              <Iconify
                icon="material-symbols:filter-alt-outline"
                className="text-gray-500"
                width={20}
              />
              <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
              <select
                className="border rounded-md px-2 py-1 text-sm text-gray-700"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center space-x-2">
              <Iconify icon="mdi:sort-variant" className="text-gray-500" width={20} />
              <span className="text-sm font-medium text-gray-700">Ordenar por:</span>
              <select
                className="border rounded-md px-2 py-1 text-sm text-gray-700"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="recent">Mais recente</option>
                <option value="alphabetical">Ordem alfabética</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex space-x-2">
            {/* Logs Button */}
            <DialogLogsAutomacao user={user} />
            {/* Create Button */}
            <DialogCriarAutomacao
              user={user}
              onCallback={data => {
                setAutomacoes([...automacoes, data]);
              navigate(`/dashboard/automacoes/${data._id}`);
              }}
            />
          </div>
        </div>

        {/* Automations List */}
        <div className="grid grid-cols-2 gap-2">
          {filteredAutomacoes.map((automation, index) => (
            <CardAutomacao
              key={index}
              onSelect={() => navigate(`/dashboard/automacoes/${automation._id}`)}
              onDelete={() => deleteAutomacao(automation._id)}
              automacao={automation}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
